// NEETPracticeTest.js
// NEET Practice Test Component - Provides NEET practice questions from local question banks
// Features: Three sections (Physics, Chemistry, Biology), difficulty levels, progress tracking

import React, { useState, useEffect, useRef } from 'react';
import './NEETPracticeTest.css';
import 'katex/dist/katex.min.css';
import katex from 'katex';

const NEETPracticeTest = ({ onComplete, onClose, savedProgress = null }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanation, setShowExplanation] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [questionsPerTest, setQuestionsPerTest] = useState(10);
  const [testStarted, setTestStarted] = useState(false);
  const [score, setScore] = useState(null);
  const [reviewMode, setReviewMode] = useState(false);

  // Timer states
  const [testMode, setTestMode] = useState('focused'); // 'focused' or 'full-length'
  const [timedMode, setTimedMode] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timeExpired, setTimeExpired] = useState(false);
  const timerIntervalRef = useRef(null);

  // Import local NEET questions from all subjects
  const neetPhysicsQuestions = require('./data/neet-physics.json');
  const neetChemistryQuestions = require('./data/neet-chemistry.json');
  const neetBiologyQuestions = require('./data/neet-biology.json');

  // Load all NEET questions
  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = () => {
    try {
      setLoading(true);
      setError(null);

      // Combine all NEET sections
      const allQuestions = [
        ...neetPhysicsQuestions,
        ...neetChemistryQuestions,
        ...neetBiologyQuestions
      ];

      console.log('Physics questions:', neetPhysicsQuestions.length);
      console.log('Chemistry questions:', neetChemistryQuestions.length);
      console.log('Biology questions:', neetBiologyQuestions.length);
      console.log('Total NEET questions:', allQuestions.length);

      if (allQuestions.length === 0) {
        throw new Error('No questions available');
      }

      setQuestions(allQuestions);
      setLoading(false);
    } catch (err) {
      console.error('Error loading NEET questions:', err);
      setError(`Failed to load NEET practice questions: ${err.message}`);
      setLoading(false);
    }
  };

  // Get available domains from questions
  const getAvailableDomains = () => {
    if (!Array.isArray(questions) || questions.length === 0) {
      return [];
    }
    const domains = [...new Set(questions.map(q => q.domain))];
    return domains.sort();
  };

  // Filter questions based on selections
  const getFilteredQuestions = () => {
    if (!Array.isArray(questions) || questions.length === 0) {
      return [];
    }

    let filtered = questions;

    if (selectedDomain !== 'all') {
      filtered = filtered.filter(q => q.domain === selectedDomain);
    }

    if (selectedDifficulty !== 'all') {
      filtered = filtered.filter(q => q.difficulty === selectedDifficulty);
    }

    return filtered;
  };

  // Start test with selected configuration
  const startTest = () => {
    const filtered = getFilteredQuestions();

    if (filtered.length === 0) {
      setError('No questions match your selections. Please adjust your filters.');
      return;
    }

    // Shuffle and limit questions
    const shuffled = [...filtered].sort(() => Math.random() - 0.5);
    const limitedQuestions = shuffled.slice(0, Math.min(questionsPerTest, shuffled.length));

    setQuestions(limitedQuestions);
    setTestStarted(true);
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setShowExplanation(false);
    setScore(null);

    // Set up timer if timed mode is enabled
    if (timedMode) {
      let timeInSeconds;
      if (testMode === 'full-length') {
        // NEET full test: 180 minutes (3 hours) for all sections
        // Each section gets 60 minutes
        timeInSeconds = 180 * 60; // Default full test time
        if (selectedDomain === 'Physics') timeInSeconds = 60 * 60;
        else if (selectedDomain === 'Chemistry') timeInSeconds = 60 * 60;
        else if (selectedDomain === 'Biology') timeInSeconds = 60 * 60;
      } else {
        // Focused practice: approximately 1 minute per question
        timeInSeconds = limitedQuestions.length * 60;
      }

      setTimeRemaining(timeInSeconds);
      setTimeExpired(false);
    }
  };

  // Math rendering function
  const renderMath = (text) => {
    if (!text) return '';

    try {
      let rendered = text;

      // Display mode ($$...$$)
      rendered = rendered.replace(/\$\$([\s\S]+?)\$\$/g, (match, math) => {
        try {
          return katex.renderToString(math, {
            displayMode: true,
            throwOnError: false,
            trust: true
          });
        } catch (e) {
          console.error('KaTeX display error:', e);
          return match;
        }
      });

      // Inline mode ($...$)
      rendered = rendered.replace(/\$([^$]+?)\$/g, (match, math) => {
        try {
          return katex.renderToString(math, {
            displayMode: false,
            throwOnError: false,
            trust: true
          });
        } catch (e) {
          console.error('KaTeX inline error:', e);
          return match;
        }
      });

      return rendered;
    } catch (error) {
      console.error('Error rendering math:', error);
      return text;
    }
  };

  // Timer countdown effect
  useEffect(() => {
    if (testStarted && timedMode && timeRemaining > 0) {
      timerIntervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setTimeExpired(true);
            alert('⏰ Time expired! You can still continue and complete the test.');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
    }
  }, [testStarted, timedMode, timeRemaining]);

  // Format time remaining
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle answer selection
  const handleAnswerSelect = (answerIndex) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: answerIndex
    }));
    setShowExplanation(false);
  };

  // Check answer
  const checkAnswer = () => {
    setShowExplanation(true);
  };

  // Navigate to next question
  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setShowExplanation(false);
    }
  };

  // Navigate to previous question
  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setShowExplanation(false);
    }
  };

  // Jump to specific question
  const jumpToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setShowExplanation(false);
  };

  // Calculate and submit test
  const submitTest = () => {
    const correctCount = questions.filter((q, idx) =>
      selectedAnswers[idx] === q.correct_answer
    ).length;

    const percentage = (correctCount / questions.length) * 100;

    // NEET scoring: 4 marks for correct, -1 for wrong. Max 720 for 180 questions
    // Simplified: proportional to 720
    const neetScore = Math.round((correctCount / questions.length) * 720);

    setScore({
      correct: correctCount,
      total: questions.length,
      percentage: percentage.toFixed(1),
      neetScore: neetScore
    });

    setTestCompleted(true);

    // Clear timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    // Call onComplete callback if provided
    if (onComplete) {
      onComplete({
        score: neetScore,
        correct: correctCount,
        total: questions.length,
        domain: selectedDomain,
        difficulty: selectedDifficulty,
        timestamp: new Date().toISOString()
      });
    }
  };

  // Review answers
  const reviewAnswers = () => {
    setReviewMode(true);
    setCurrentQuestionIndex(0);
  };

  // Restart test
  const restartTest = () => {
    setTestStarted(false);
    setTestCompleted(false);
    setReviewMode(false);
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setShowExplanation(false);
    setScore(null);
    setTimeRemaining(0);
    setTimeExpired(false);
    loadQuestions();
  };

  const currentQuestion = questions[currentQuestionIndex];
  const selectedAnswer = selectedAnswers[currentQuestionIndex];
  const isAnswered = selectedAnswer !== undefined;
  const isCorrect = isAnswered && selectedAnswer === currentQuestion?.correct_answer;
  const availableDomains = getAvailableDomains();

  // Loading state
  if (loading) {
    return (
      <div className="neet-practice-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading NEET practice questions...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="neet-practice-container">
        <div className="error-state">
          <h3>⚠️ Error</h3>
          <p>{error}</p>
          <button onClick={restartTest} className="retry-button">Try Again</button>
        </div>
      </div>
    );
  }

  // Test completed view
  if (testCompleted && !reviewMode) {
    return (
      <div className="neet-practice-container">
        <div className="results-container">
          <h2>🎉 Test Complete!</h2>
          <div className="score-display">
            <div className="score-main">
              <div className="act-score">
                <span className="score-label">NEET Score</span>
                <span className="score-value">{score.neetScore}</span>
                <span className="score-range">out of 720</span>
              </div>
            </div>
            <div className="score-details">
              <div className="score-stat">
                <span className="stat-label">Correct Answers</span>
                <span className="stat-value">{score.correct} / {score.total}</span>
              </div>
              <div className="score-stat">
                <span className="stat-label">Percentage</span>
                <span className="stat-value">{score.percentage}%</span>
              </div>
            </div>
          </div>
          <div className="results-actions">
            <button onClick={reviewAnswers} className="review-button">Review Answers</button>
            <button onClick={restartTest} className="restart-button">New Test</button>
            <button onClick={onClose} className="close-button">Close</button>
          </div>
        </div>
      </div>
    );
  }

  // Setup screen
  if (!testStarted) {
    return (
      <div className="neet-practice-container">
        <div className="setup-container">
          <h2>NEET Practice Test Setup</h2>
          <p className="setup-description">
            Customize your NEET practice test by selecting section, difficulty, and test mode.
          </p>

          <div className="setup-section">
            <label>Test Section:</label>
            <select value={selectedDomain} onChange={(e) => setSelectedDomain(e.target.value)} className="setup-select">
              <option value="all">All Sections</option>
              {availableDomains.map(domain => (
                <option key={domain} value={domain}>{domain}</option>
              ))}
            </select>
            <p className="setup-hint info">✅ Available: Physics, Chemistry, Biology (30 total questions)</p>
          </div>

          <div className="setup-section">
            <label>Difficulty Level:</label>
            <select value={selectedDifficulty} onChange={(e) => setSelectedDifficulty(e.target.value)} className="setup-select">
              <option value="all">All Levels</option>
              <option value="Easy">Easy</option>
              <option value="Medium">Medium</option>
              <option value="Hard">Hard</option>
            </select>
          </div>

          <div className="setup-section">
            <label>Number of Questions:</label>
            <input
              type="number"
              min="5"
              max={getFilteredQuestions().length}
              value={questionsPerTest}
              onChange={(e) => setQuestionsPerTest(parseInt(e.target.value) || 10)}
              className="setup-input"
            />
            <p className="setup-hint">{getFilteredQuestions().length} questions available with current filters</p>
          </div>

          <div className="setup-section">
            <label>Test Mode:</label>
            <select value={testMode} onChange={(e) => setTestMode(e.target.value)} className="setup-select">
              <option value="focused">Focused Practice (Untimed/Custom Time)</option>
              <option value="full-length">Full-Length Simulation (Timed)</option>
            </select>
          </div>

          <div className="setup-section">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={timedMode}
                onChange={(e) => setTimedMode(e.target.checked)}
              />
              <span>Enable Timer</span>
            </label>
            {timedMode && (
              <p className="setup-hint">
                {testMode === 'full-length'
                  ? selectedDomain === 'all'
                    ? '⏱️ Time limit: 3 hours (full NEET)'
                    : '⏱️ Time limit: 60 minutes (per section)'
                  : `⏱️ Time limit: ${questionsPerTest} minutes (~1 min/question)`
                }
              </p>
            )}
          </div>

          <div className="setup-actions">
            <button onClick={startTest} className="start-button">Start NEET Practice Test</button>
            <button onClick={onClose} className="cancel-button">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // Test in progress view
  return (
    <div className="neet-practice-container">
      {/* Header with progress */}
      <div className="test-header">
        <div className="progress-info">
          <span className="question-counter">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
        {timedMode && timeRemaining > 0 && (
          <div className={`timer-badge ${timeExpired ? 'timer-expired' : timeRemaining < 300 ? 'timer-warning' : ''}`}>
            ⏱️ {formatTime(timeRemaining)}
          </div>
        )}
      </div>

      {/* Question content */}
      <div className="question-container">
        <div className="question-meta">
          <span className="question-domain">{currentQuestion.domain}</span>
          <span className="question-difficulty">{currentQuestion.difficulty}</span>
          {currentQuestion.skill && <span className="question-skill">{currentQuestion.skill}</span>}
        </div>

        {currentQuestion.passage && (
          <div className="question-passage">
            <div dangerouslySetInnerHTML={{ __html: renderMath(currentQuestion.passage) }} />
          </div>
        )}

        <div
          className="question-text"
          dangerouslySetInnerHTML={{ __html: renderMath(currentQuestion.question) }}
        />

        <div className="answer-choices">
          {currentQuestion.choices.map((choice, idx) => {
            const isSelected = selectedAnswer === idx;
            const showCorrect = showExplanation && idx === currentQuestion.correct_answer;
            const showIncorrect = showExplanation && isSelected && idx !== currentQuestion.correct_answer;

            return (
              <button
                key={idx}
                onClick={() => !showExplanation && handleAnswerSelect(idx)}
                className={`answer-choice ${isSelected ? 'selected' : ''} ${showCorrect ? 'correct' : ''} ${showIncorrect ? 'incorrect' : ''}`}
                disabled={showExplanation}
              >
                <span className="choice-letter">{String.fromCharCode(65 + idx)}</span>
                <span
                  className="choice-text"
                  dangerouslySetInnerHTML={{ __html: renderMath(choice) }}
                />
                {showCorrect && <span className="choice-indicator">✓</span>}
                {showIncorrect && <span className="choice-indicator">✗</span>}
              </button>
            );
          })}
        </div>

        {showExplanation && (
          <div className={`explanation ${isCorrect ? 'correct-explanation' : 'incorrect-explanation'}`}>
            <h4>{isCorrect ? '✅ Correct!' : '❌ Incorrect'}</h4>
            <p>{currentQuestion.explanation}</p>
          </div>
        )}
      </div>

      {/* Navigation controls */}
      <div className="test-controls">
        <button
          onClick={prevQuestion}
          disabled={currentQuestionIndex === 0}
          className="nav-button"
        >
          ← Previous
        </button>

        {!showExplanation && isAnswered && (
          <button onClick={checkAnswer} className="check-button">
            Check Answer
          </button>
        )}

        {currentQuestionIndex < questions.length - 1 ? (
          <button onClick={nextQuestion} className="nav-button">
            Next →
          </button>
        ) : (
          <button onClick={submitTest} className="submit-button">
            Submit Test
          </button>
        )}
      </div>

      {/* Question navigator */}
      <div className="question-navigator">
        <p className="navigator-title">Jump to Question:</p>
        <div className="question-grid">
          {questions.map((_, idx) => (
            <button
              key={idx}
              onClick={() => jumpToQuestion(idx)}
              className={`question-nav-btn ${idx === currentQuestionIndex ? 'current' : ''} ${selectedAnswers[idx] !== undefined ? 'answered' : ''}`}
            >
              {idx + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NEETPracticeTest;
